import {
  CheckIcon,
  ChevronDownIcon,
  IconSizeEnum,
  StateCircleIcon,
  Tooltip,
} from '@acceso/design-system';
import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { REVIEW_STATES, TReviewStates } from '@/constants';
import { ReviewStateEnum, TReviewStateValues } from '@/features/segments/types';

import { StatusSeenChipInTable } from '../StatusSeenChipInTable';

interface CustomSelectProps<T = ReviewStateEnum> {
  value: number | null;
  className?: string;
  buttonClassName?: string;
  asIcon?: boolean;
  placeholder?: string;
  onChange: (value: string) => void;
  listOrientation?: 'left' | 'right';
  isProgram?: boolean;
  onMenuClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  reviewStates?: TReviewStates<T>[];
}

export function StatusSeenChipSelect<T = ReviewStateEnum>({
  value,
  onChange,
  className,
  buttonClassName,
  asIcon = false,
  placeholder,
  listOrientation = 'right',
  isProgram = false,
  reviewStates = REVIEW_STATES as unknown as TReviewStates<T>[],
  onMenuClick = () => {},
}: CustomSelectProps<T>) {
  const [selectedValue, setSelectedValue] = useState<string>(
    value !== null ? value.toString() : ''
  );
  const [open, setOpen] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();

  const handleOptionClick = (optionValue: string) => {
    setSelectedValue(optionValue);
    onChange(optionValue);
    setOpen(false);
  };

  const selectedState = useMemo(
    () =>
      value !== null ? (reviewStates.find(review => Number(review.value) === value) ?? null) : null,
    [value, reviewStates]
  );

  useEffect(() => {
    setSelectedValue(value !== null ? value.toString() : '');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredStates = useMemo(() => {
    if (isProgram && value === 6) {
      return reviewStates.filter(state => state.value === 5 || state.value === 6);
    }
    return reviewStates;
  }, [isProgram, value, reviewStates]);

  const SelectedIcon = selectedState?.icon ?? null;

  return (
    <div className={clsx('relative inline-block items-center', className)} ref={containerRef}>
      {!asIcon ? (
        <button
          type="button"
          onClick={() => setOpen(prev => !prev)}
          className={clsx(
            'appareance-none flex w-[120px] gap-1 rounded-sm px-2 py-1',
            value === null || !selectedState
              ? 'border border-dashed border-gray-300 bg-gray-50 text-gray-400'
              : selectedState.class,
            buttonClassName
          )}
        >
          {value === null || !selectedState ? (
            <span className="text-xs">{placeholder ?? t('Seleccionar estado')}</span>
          ) : (
            <>
              {SelectedIcon && <SelectedIcon size={IconSizeEnum.XS} />}
              <span className="text-xs font-semibold">{t(selectedState.label)}</span>
            </>
          )}
        </button>
      ) : (
        <Tooltip help={t('Revisión')} grow>
          <StateCircleIcon
            onClick={() => setOpen(prev => !prev)}
            size={IconSizeEnum.SM}
            className="cursor-pointer"
          />
        </Tooltip>
      )}

      {open && (
        <div
          className={clsx(
            'absolute z-50 mt-1 w-[150px] rounded-md border border-gray-200 bg-white shadow-lg',
            listOrientation === 'left' ? 'right-0' : 'left-0'
          )}
          onClick={onMenuClick}
          onMouseDown={onMenuClick}
        >
          {filteredStates.map(review => {
            const key = (review.value as unknown as number).toString();
            return (
              <div
                key={key}
                onClick={() => handleOptionClick(key)}
                className={clsx(
                  'flex w-full cursor-pointer items-center gap-1 px-2 py-1 hover:bg-gray-100'
                )}
              >
                <StatusSeenChipInTable
                  review_state={parseInt(key) as TReviewStateValues}
                  classname="flex-1"
                  reviewStates={reviewStates}
                />
                {selectedValue === key && <CheckIcon size={IconSizeEnum.XS} />}
              </div>
            );
          })}
        </div>
      )}

      {!asIcon && (
        <ChevronDownIcon
          onClick={() => setOpen(prev => !prev)}
          size={IconSizeEnum.XS}
          className={clsx(
            'absolute right-2 top-1/2 -translate-y-1/2 transform cursor-pointer',
            open && '-rotate-180'
          )}
        />
      )}
    </div>
  );
}
